import { useMemo } from 'react';
import { 
  isStripeEnabled, 
  getDefaultPaymentProvider, 
  isStripeOrder, 
  isAuthorizeNetOrder,
  getOrderPaymentProvider 
} from '../payment-config';

interface UsePaymentProviderProps {
  order?: any;
  catererData?: any;
}

export const usePaymentProvider = ({ order, catererData }: UsePaymentProviderProps = {}) => {
  const config = useMemo(() => ({
    isStripeEnabled: isStripeEnabled(),
    defaultProvider: getDefaultPaymentProvider(),
  }), []);

  const orderProvider = useMemo(() => {
    if (!order) return null;
    return getOrderPaymentProvider(order);
  }, [order]);

  const canUseStripe = useMemo(() => {
    if (!config.isStripeEnabled) return false;
    // Use Stripe for all caterers when enabled, regardless of connected account status
    return true;
  }, [config.isStripeEnabled]);

  const canUseAuthorizeNet = useMemo(() => {
    // Authorize.net is always available as fallback
    return true;
  }, []);

  const recommendedProvider = useMemo(() => {
    if (canUseStripe) return 'stripe';
    if (canUseAuthorizeNet) return 'authorizeNet';
    return null;
  }, [canUseStripe, canUseAuthorizeNet]);

  const getPaymentButtonText = (isEdit: boolean = false) => {
    if (config.defaultProvider === 'stripe') {
      return isEdit ? 'Confirm Edited Order' : 'Pay with Stripe';
    }
    return isEdit ? 'Confirm Edited Order' : 'Confirm and Place Order';
  };

  const getPaymentButtonIcon = () => {
    if (config.defaultProvider === 'stripe') {
      return '../assets/images/icon-cart.svg';
    }
    return '../assets/images/icon-cart.svg';
  };

  return {
    config,
    orderProvider,
    canUseStripe,
    canUseAuthorizeNet,
    recommendedProvider,
    getPaymentButtonText,
    getPaymentButtonIcon,
    isStripeOrder: order ? isStripeOrder(order) : false,
    isAuthorizeNetOrder: order ? isAuthorizeNetOrder(order) : false,
  };
}; 